// Settings → Audit Log (Wave E).
//
// Filters (URL params): action, staff_id, date_from, date_to.
// Backed by GET /api/v1/activity-log. Admin-only at the route layer.
// Wrapped in <SubscriptionGate feature="audit-log"> by the router.

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, History } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { FilterBar } from '../../../../components/ui/FilterBar/FilterBar';
import { Table, type Column } from '../../../../components/ui/Table/Table';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import { Input } from '../../../../components/form/Input';
import { DatePicker } from '../../../../components/form/DatePicker';
import { useAuditLog } from './hooks/useAuditLog';
import type { ActivityLogEntry } from '../../../../types/api';
import styles from './AuditLogPage.module.css';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AuditLogPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const action = params.get('action') ?? '';
  const staffIdRaw = params.get('staff_id') ?? '';
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const filters = {
    page,
    action: action || undefined,
    staff_id: staffIdRaw ? Number(staffIdRaw) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const list = useAuditLog(filters);

  const logs = list.data?.logs ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<ActivityLogEntry>[] = useMemo(
    () => [
      {
        key: 'created_at',
        header: 'When',
        render: (_v, row) => formatDateTime(row.created_at),
      },
      { key: 'action', header: 'Action' },
      {
        key: 'details',
        header: 'Details',
        render: (_v, row) => row.details ?? '—',
      },
      {
        key: 'entity_type',
        header: 'Entity',
        render: (_v, row) =>
          row.entity_type ? <span>{row.entity_type}</span> : '—',
      },
      {
        key: 'staff_id',
        header: 'Actor',
        render: (_v, row) => {
          if (row.staff_id) return `staff:${row.staff_id}`;
          if (row.driver_id) return `driver:${row.driver_id}`;
          return '—';
        },
      },
      {
        key: 'ip_address',
        header: 'IP',
        render: (_v, row) => row.ip_address ?? '—',
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Audit Log"
        subtitle={`${total} entr${total === 1 ? 'y' : 'ies'}`}
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/reports')}
          >
            Back
          </Button>
        }
      />

      <Card flush>
        <FilterBar ariaLabel="Audit log filters">
          <Input
            aria-label="Action filter"
            placeholder="Action (e.g. user_created)"
            value={action}
            onChange={(e) => setParam('action', e.target.value)}
          />
          <Input
            aria-label="Staff ID filter"
            variant="number"
            placeholder="Staff ID"
            value={staffIdRaw}
            onChange={(e) => setParam('staff_id', e.target.value)}
          />
          <DatePicker
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setParam('date_from', e.target.value)}
          />
          <DatePicker
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setParam('date_to', e.target.value)}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={logs as ActivityLogEntry[]}
          keyField={(row) => row.id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <History size={28} aria-hidden />
              <h3>No audit entries</h3>
              <p>Adjust your filters or check a wider date range.</p>
            </div>
          }
          ariaLabel="Audit log"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
